import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useIsPlatformAdmin } from '../../../../hooks/useAuth'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { BillingInvoiceBadge } from '../../../../components/admin/BillingInvoiceBadge'
import { BillingInvoiceActions } from '../../../../components/admin/BillingInvoiceActions'
import { BillingInvoiceNextStep } from '../../../../components/admin/BillingInvoiceNextStep'
import { BillingInvoiceLines } from '../../../../components/admin/BillingInvoiceLines'
import { BillingInvoiceYear } from '../../../../components/admin/BillingInvoiceYear'
import { BillingInvoicePayments } from '../../../../components/admin/BillingInvoicePayments'
import { invoicePeriod } from '../../../../components/admin/BillingInvoicesTable'
import { formatCents, useAdminBillingInvoice } from '../../../../hooks/use-admin-billing'

export const Route = createFileRoute('/admin/billing/invoices/$uid')({
  component: RouteComponent,
})

function Field({ label, value, testid }: { label: string; value: React.ReactNode; testid?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums" data-testid={testid}>
        {value}
      </div>
    </div>
  )
}

// One invoice: lines, reductions, payments and the actions of its lifecycle
// (spec 2026-10-05-14). Platform admins only.
function RouteComponent() {
  const { t, i18n } = useTranslation('admin')
  const { uid } = Route.useParams()
  const { loading, orgCode } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const navigate = useNavigate({ from: '/admin/billing/invoices/$uid' })
  const { data: inv, isLoading, error } = useAdminBillingInvoice(uid)

  useEffect(() => {
    if (!loading && !isPlatformAdmin) {
      navigate({ to: '/', replace: true })
    }
  }, [loading, isPlatformAdmin, navigate])

  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('billing.title'), path: '/admin/billing' },
    { title: t('billing.invoices.title'), path: '/admin/billing/invoices' },
    { title: inv?.number ?? (inv ? invoicePeriod(inv) : uid) },
  ])

  if (loading || !isPlatformAdmin) {
    return null
  }
  const money = (c: number) => formatCents(c, i18n.language)

  return (
    <div className="space-y-6" data-testid="admin-billing-invoice-page">
      <PageHeader
        icon={SECTION_ICONS.adminBilling}
        title={inv ? `${t(`billing.invoices.kind.${inv.kind}`)} ${invoicePeriod(inv)}` : t('billing.invoices.title')}
        description={inv ? `${inv.organization_name || inv.organization_code}${inv.number ? ` · ${inv.number}` : ''}` : undefined}
      />

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="billing-error">
          {t('billing.invoices.load_error')}
        </div>
      )}
      {isLoading && <div className="text-muted-foreground">{t('billing.loading')}</div>}

      {inv && (
        <>
          <BillingInvoiceNextStep invoice={inv} />
          {inv.status === 'draft' && <BillingInvoiceActions invoice={inv} part="issue" />}
          <section className="bg-card rounded-lg border border-border p-4 space-y-4" data-testid="billing-invoice-summary">
            <div className="flex flex-wrap items-center gap-3">
              <BillingInvoiceBadge state={inv.payment_state} />
              <Link
                to="/admin/billing/$orgUid"
                params={{ orgUid: inv.organization_uid }}
                className="text-sm text-primary hover:underline"
                data-testid="billing-invoice-org-link"
              >
                {inv.organization_name || inv.organization_code}
              </Link>
              {inv.parent_invoice_uid && (
                <Link
                  to="/admin/billing/invoices/$uid"
                  params={{ uid: inv.parent_invoice_uid }}
                  className="text-sm text-primary hover:underline"
                  data-testid="billing-invoice-parent-link"
                >
                  {t('billing.invoices.parent_link', { number: inv.parent_number ?? t('billing.invoices.no_number') })}
                </Link>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field label={t('billing.invoices.column.number')} value={inv.number ?? '—'} testid="billing-invoice-number" />
              <Field label={t('billing.invoices.column.period')} value={invoicePeriod(inv)} />
              <Field label={t('billing.invoices.issued_on')} value={inv.issued_on ?? '—'} />
              <Field label={t('billing.invoices.column.due_on')} value={inv.due_on ?? '—'} testid="billing-invoice-due-on" />
              <Field label={t('billing.invoices.total_excl_vat')} value={money(inv.total_excl_vat_cents)} testid="billing-invoice-total-excl" />
              <Field label={t('billing.invoices.vat', { rate: inv.vat_rate_bp / 100 })} value={money(inv.vat_cents)} testid="billing-invoice-vat" />
              <Field label={t('billing.invoices.column.total')} value={money(inv.total_cents)} testid="billing-invoice-total" />
              <Field label={t('billing.invoices.paid')} value={money(inv.paid_cents)} testid="billing-invoice-paid" />
              {inv.status === 'issued' && (
                <Field label={t('billing.invoices.column.remaining')} value={money(inv.remaining_cents)} testid="billing-invoice-remaining" />
              )}
              {inv.status === 'cancelled' && (
                <Field
                  label={t('billing.invoices.cancelled')}
                  value={[inv.credit_note_number, inv.cancel_reason].filter(Boolean).join(' · ') || '—'}
                  testid="billing-invoice-cancelled"
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t('billing.invoices.legal_note')}</p>
          </section>

          <BillingInvoiceLines invoice={inv} />
          {inv.kind === 'annual' && <BillingInvoiceYear invoice={inv} />}
          <BillingInvoicePayments invoice={inv} />
          <BillingInvoiceActions invoice={inv} part="cancel" />
        </>
      )}
    </div>
  )
}
